import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetch2faStatus } from '../api';
import { TwoFactorEnroll } from '../components/TwoFactorEnroll';
import { Spinner } from '../components/Spinner';
import { RotateCw } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

/**
 * Mandatory 2FA enrollment gate. RequireTwoFactor redirects here (instead of
 * dumping the user into full Settings) whenever the deployment requires 2FA and
 * it isn't enabled yet — covering both fresh signup and any later un-enrolled
 * login. Full-screen, onboarding-styled. Already-enrolled visitors bounce to /.
 */
export function EnrollTwoFactorPage() {
  useDocumentTitle('Secure your account');
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['2fa-status'],
    queryFn: fetch2faStatus,
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="grid min-h-screen place-items-center text-ink-muted">
        <Spinner />
      </div>
    );
  }

  // Nothing to do here once 2FA is on — send them into the app.
  if (data?.enabled) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="mb-8 flex flex-col items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-ink text-white">
            <RotateCw className="h-6 w-6" aria-hidden="true" />
          </span>
          <div className="text-center">
            <h1 className="text-lg font-semibold tracking-tight text-ink">
              Secure your account
            </h1>
            <p className="mt-1 text-sm text-ink-muted">
              This platform requires two-factor authentication. Enroll now to
              finish setting up your account.
            </p>
          </div>
        </div>

        <TwoFactorEnroll
          required={data?.required ?? true}
          onEnrolled={() => navigate('/', { replace: true })}
        />
      </div>
    </div>
  );
}
